import React, { useEffect, useState } from "react";
import { CheckCircle, Upload, Loader2 } from "lucide-react";
import { LEAD_STAGE, automationNodeLeadStageToRouteMap } from "../../Utils/constants";
import useLeadStage from "../../hooks/useLeadStage";
import { useNavigate } from "react-router-dom";
import { squidloansUserAPI } from "../../services/api";
import PageLoader from "../../components/Loader";
import FlowFooter from "../../components/FlowFooter";

const curr_page_lead_stage = [
  LEAD_STAGE.PENDING_EMPLOYEMENT_WITH_SELF_EMPLOYEED,
  LEAD_STAGE.BRE_REJECT,
  LEAD_STAGE.DISBURSAL_FAILED,
  LEAD_STAGE.REJECT_AADHAR_KYC
];

const ThankYouCard = () => {
  const [animateIn, setAnimateIn] = useState(false);
  const { leadStage } = useLeadStage();
  const navigate = useNavigate();
  const [journeyData, setJourneyData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateIn(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchJourney = async () => {
      try {
        const data = await squidloansUserAPI.getJourney();
        setJourneyData(data?.journey || null);
        const newLeadStage = data?.journey?.lead_stage || null;
        // console.log("newLeadStage", newLeadStage);
        if (!curr_page_lead_stage.includes(newLeadStage)) {
          navigate(automationNodeLeadStageToRouteMap[newLeadStage]);
        }
      } catch (err) {
        console.error("Error fetching journey step:", err);
      }
    };

    fetchJourney();
    const interval = setInterval(fetchJourney, 5000);

    return () => clearInterval(interval);
  }, [navigate]);

  if (!leadStage) {
    return <PageLoader />;
  }

  if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
    navigate(automationNodeLeadStageToRouteMap[leadStage]);
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (images and PDFs)
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setUploadError('Please upload an image (JPG, PNG, WEBP) or PDF file');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('File size should be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      await squidloansUserAPI.uploadAddressProof(selectedFile);
      setUploadSuccess(true);
      setSelectedFile(null);
      // Refresh journey data to update address_proof_uploaded status
      const data = await squidloansUserAPI.getJourney();
      setJourneyData(data?.journey || null);
    } catch (error) {
      setUploadError(error.message || 'Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Check if we should use UploadBankStatement color scheme
  const isBRERejectWithoutAddressProof = journeyData &&
    journeyData.lead_stage === LEAD_STAGE.BRE_REJECT &&
    !journeyData.address_proof_uploaded;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-xl w-full mx-auto flex items-center justify-center min-h-[60vh] space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10 w-full">
              {isBRERejectWithoutAddressProof ? (
                <div className="w-full flex flex-col items-center">
                <div
                  className={`mx-auto w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 flex items-center justify-center mb-4 sm:mb-6 transition-all duration-1000 ${animateIn ? "scale-100 opacity-100" : "scale-50 opacity-0"}`}
                >
                  <div className="absolute animate-ping w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-[#FF6B35]/20"></div>
                  <CheckCircle size={48} className="sm:w-16 sm:h-16 md:w-20 md:h-20 relative text-[#FF6B35]" />
                </div>

                <h1
                  className={`text-lg sm:text-xl md:text-2xl font-bold text-center mb-3 sm:mb-4 text-[#1E3A8A] transition-all duration-700 delay-300 ${animateIn ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}
                >
                  Your application is with our Credit OGs!
                </h1>

                {/* Address Proof Upload Section */}
                {journeyData && !journeyData.address_proof_uploaded && journeyData.lead_stage === LEAD_STAGE.BRE_REJECT && (
                  <div className={`w-full mb-4 sm:mb-6 transition-all duration-700 delay-500 ${animateIn ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
                    <div className="border-2 border-dashed border-[#FF6B35]/30 rounded-xl p-4 sm:p-6 bg-[#F8FAFC]">
                      <p className="text-center text-[#1E293B] font-semibold mb-2 text-xs sm:text-sm md:text-base">
                        Help us process your loan faster
                      </p>
                      <p className="text-center text-[#64748B] text-xs sm:text-sm mb-3 sm:mb-4">
                        Please provide document that includes your <span className="font-bold text-[#FF6B35]">current</span> address like <span className="font-bold text-[#FF6B35]">electricity bill</span>, <span className="font-bold text-[#FF6B35]">gas bill</span>
                      </p>

                      {uploadSuccess ? (
                        <div className="text-center text-green-600 text-xs sm:text-sm mb-3">
                          ✓ Address proof uploaded successfully!
                        </div>
                      ) : (
                        <>
                          <label className="flex flex-col items-center cursor-pointer">
                            <div className="flex items-center justify-center w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-[#E2E8F0] rounded-xl hover:bg-[#F8FAFC] transition-colors">
                              <Upload className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0 text-[#FF6B35]" />
                              <span className="text-xs sm:text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis text-[#1E293B]">
                                {selectedFile ? selectedFile.name : "Choose file (Image or PDF)"}
                              </span>
                            </div>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={handleFileChange}
                              className="hidden"
                              disabled={isUploading}
                            />
                          </label>

                          {uploadError && (
                            <p className="text-red-500 text-xs mt-2 text-center">{uploadError}</p>
                          )}

                          {selectedFile && !isUploading && (
                            <button
                              onClick={handleFileUpload}
                              className="w-full mt-3 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white font-semibold rounded-xl transition-all hover:opacity-90 text-xs sm:text-sm shadow-md"
                            >
                              Upload File
                            </button>
                          )}

                          {isUploading && (
                            <div className="flex items-center justify-center mt-3 text-[#FF6B35]">
                              <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin mr-2" />
                              <span className="text-xs sm:text-sm">Uploading...</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                <p
                  className={`text-center text-[#64748B] mb-4 sm:mb-6 text-xs sm:text-sm md:text-base transition-all duration-700 delay-600 ${animateIn ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}
                >
                  Sit tight — we'll get back to you soon. Need anything meanwhile? Reach us at <br /> <a href="tel:+918750238443" className="font-semibold text-[#FF6B35] hover:text-[#E55A2B] transition-colors">8750238443</a>.
                </p>
              </div>
              ) : (
                <>
                  <div className={`mx-auto w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 flex items-center justify-center mb-4 sm:mb-6 transition-all duration-1000 ${animateIn ? "scale-100 opacity-100" : "scale-50 opacity-0"}`}>
                    <div className="absolute animate-ping w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-[#FF6B35]/20"></div>
                    <CheckCircle size={48} className="sm:w-16 sm:h-16 md:w-20 md:h-20 relative text-[#FF6B35]" />
                  </div>

                  <h1
                    className={`text-lg sm:text-xl md:text-2xl font-bold text-center mb-3 sm:mb-4 text-[#1E3A8A] transition-all duration-700 delay-300 ${animateIn ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}
                  >
                    Your application is with our Credit OGs!
                  </h1>

                  <p
                    className={`text-center text-[#64748B] mb-4 sm:mb-6 text-xs sm:text-sm md:text-base transition-all duration-700 delay-500 ${animateIn ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}
                  >
                    Sit tight — we'll get back to you soon. Need anything meanwhile? Reach us at <br /> <a href="tel:+918750238443" className="font-semibold text-[#FF6B35] hover:text-[#E55A2B] transition-colors">8750238443</a>.
                  </p>
                </>
              )}
          </div>
        </div>
      </div>
      <FlowFooter />
    </div>
  );
};

export default ThankYouCard;
